import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box, Stack, Button, IconButton, Avatar, Menu, MenuItem, Typography,
    Divider, Drawer, List, ListItemButton, ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import { tokens } from "../api";

/*
 * The bar every signed-in page wears.
 *
 * One component rather than a copy per page: the logo moved sideways between the landing
 * page and the sign-in page exactly because each had drawn its own bar, and the next page
 * would have drifted the same way.
 *
 * Two tabs, on the right, shaped like the admin header's pair: where the customer's own
 * work is, and what it costs. More are added as their pages are built - an empty tab that
 * leads nowhere is worse than no tab.
 */
const TABS = [
    { label: "Overview", path: "/account" },
    { label: "Plans", path: "/plans" },
];

export default function AppHeader({ account }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [menu, setMenu] = useState(null);
    const [drawer, setDrawer] = useState(false);

    const logout = () => { tokens.account.clear(); navigate("/"); };

    return (
        <Box component="header" sx={{
            position: "sticky", top: 0, zIndex: 10,
            borderBottom: "1px solid rgba(255,255,255,.07)",
            bgcolor: "rgba(7,18,11,.72)", backdropFilter: "blur(10px)",
        }}>
            <Box sx={{ px: { xs: 2, md: 4, xl: 6 } }}>
                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", py: 1, minHeight: 58 }}>
                    <Stack direction="row" spacing={{ xs: 1, md: 4 }} sx={{ alignItems: "center" }}>
                        <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix"
                            sx={{ height: { xs: 26, sm: 32 }, cursor: "pointer" }}
                            onClick={() => navigate("/account")} />

                    </Stack>

                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                        {/* On the right, beside the avatar, the same shape the admin
                            header uses: quiet where you are not, lifted where you are.
                            On a phone they move into the drawer, where they have room
                            to be tapped. */}
                        {account && (
                            <Stack direction="row" spacing={1} sx={{ display: { xs: "none", sm: "flex" } }}>
                                {TABS.map((t) => {
                                    const on = pathname === t.path;
                                    return (
                                        <Button key={t.path} onClick={() => navigate(t.path)} sx={{
                                            borderRadius: 999, px: 2,
                                            color: on ? "#fff" : "rgba(255,255,255,.6)",
                                            fontWeight: on ? 700 : 500,
                                            bgcolor: on ? "rgba(255,255,255,.08)" : "transparent",
                                            "&:hover": { bgcolor: "rgba(255,255,255,.1)" },
                                        }}>
                                            {t.label}
                                        </Button>
                                    );
                                })}
                            </Stack>
                        )}

                        <IconButton onClick={(e) => setMenu(e.currentTarget)} sx={{ p: .5 }}>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: "#2E7D20", color: "#fff" }}>
                                <PersonIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                        </IconButton>

                        {TABS.length > 0 && (
                            <IconButton onClick={() => setDrawer(true)}
                                sx={{ display: { xs: "inline-flex", md: "none" }, color: "#fff" }}>
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </Box>

            <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1, minWidth: 230, bgcolor: "#0C1A12", color: "#fff",
                            border: "1px solid rgba(255,255,255,.12)", borderRadius: 2.5,
                        },
                    },
                }}>
                {/* The email, so nobody has to guess which account they are signed into -
                    the question that comes up the moment somebody has two. */}
                <Box sx={{ px: 2, py: 1.2 }}>
                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.5)" }}>Signed in as</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, wordBreak: "break-all" }}>
                        {account?.email || "—"}
                    </Typography>
                </Box>
                <Divider sx={{ borderColor: "rgba(255,255,255,.1)" }} />
                <MenuItem onClick={() => { setMenu(null); navigate("/profile"); }} sx={{ gap: 1.2, py: 1.1 }}>
                    <PersonOutlineIcon fontSize="small" /> Profile
                </MenuItem>
                <MenuItem onClick={logout} sx={{ gap: 1.2, py: 1.1 }}>
                    <LogoutIcon fontSize="small" /> Logout
                </MenuItem>
            </Menu>

            <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}
                slotProps={{ paper: { sx: { bgcolor: "#0C1A12", color: "#fff", width: 240 } } }}>
                <List sx={{ pt: 2 }}>
                    {TABS.map((t) => (
                        <ListItemButton key={t.path} selected={pathname === t.path}
                            onClick={() => { setDrawer(false); navigate(t.path); }}>
                            <ListItemText primary={t.label} />
                        </ListItemButton>
                    ))}
                    <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,.1)" }} />
                    <ListItemButton onClick={logout}>
                        <ListItemText primary="Logout" />
                    </ListItemButton>
                </List>
            </Drawer>
        </Box>
    );
}
